import React, { useState } from 'react';
import { VerbundTag } from './VerbundTag';
import { useNavigate } from 'react-router-dom';
import { zielFuer, zeilenKlick } from './ziel';
import { Wallet, FileDown, Loader2 } from 'lucide-react';
import { api } from '../../context/AuthContext';

const eur = (v) => (v || 0).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
const fmt = (iso) => { if (!iso) return '—'; try { return new Date(iso).toLocaleDateString('de-DE'); } catch { return iso; } };
const ART = { akonto: 'Akonto', raten: 'Raten', notfall: 'Notfall' };
const TON = { bezahlt: 'text-emerald-700', offen: 'text-[#b8893d]', ueberfaellig: 'text-rose-700' };

const Summe = ({ k, label, wert }) => (
  <div className="flex-1 min-w-[110px] px-3 py-2 bg-[#f5f8fa]" data-testid={`m360-zahl-summe-${k}`}>
    <p className="text-[10px] tracking-[0.15em] uppercase text-[#7a8a99]">{label}</p>
    <p className={`font-serif text-lg ${TON[k]}`}>{eur(wert)}</p>
  </div>
);

const Zeile = ({ z, mnr }) => {
  const navigate = useNavigate();
  return (
  <li onClick={zeilenKlick(navigate, zielFuer(z.art, z.id, mnr, z.case_id))} className="flex flex-wrap items-center gap-x-2 gap-y-0.5 py-1.5 px-1 text-xs cursor-pointer hover:bg-[#f5f8fa] rounded" data-testid={`m360-zahlung-${z.art}-${z.id}`}>
    <VerbundTag nr={z.verbund_nr} />
    <span className="text-[10px] tracking-wider uppercase px-1.5 py-0.5 bg-[#fbf7f0] text-[#b8893d] sm:w-16 text-center">{ART[z.art]}</span>
    <span className="flex-1 min-w-[10rem] line-clamp-2 sm:truncate text-[#1B3A5C]" title={z.titel}>{z.titel}</span>
    {z.aktenzeichen && <span className="font-mono text-[10px] text-[#34536b]">AZ {z.aktenzeichen}</span>}
    <span className={`text-[10px] ${z.ueberfaellig ? TON.ueberfaellig : z.offen ? TON.offen : z.bezahlt ? TON.bezahlt : 'text-[#7a8a99]'}`}>{z.status_label}</span>
    <span className="font-mono text-[#1B3A5C] sm:w-24 text-right">{eur(z.betrag)}</span>
    <span className="text-[10px] text-[#7a8a99] sm:w-20 text-right">{fmt(z.datum)}</span>
  </li>
  );
};

const PdfBtn = ({ mnr, caseId }) => {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(false);
  const run = async () => {
    setBusy(true); setErr(false);
    try {
      const r = await api.get(`/register/mandanten/${mnr}/zahlungen.pdf`, { params: { case_id: caseId || undefined }, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a'); a.href = url; a.download = `Zahlungen_M-${mnr}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { setErr(true); }
    setBusy(false);
  };
  return (
    <button type="button" onClick={run} disabled={busy} data-testid="m360-zahlungen-pdf" title="Zahlungsübersicht (aktuelle Akten-Auswahl) als PDF"
      className={`ml-auto inline-flex items-center gap-1.5 px-3 py-1 text-[10px] tracking-wider border transition-colors ${err ? 'border-rose-300 text-rose-700' : 'border-[#1B3A5C] text-[#1B3A5C] hover:bg-[#1B3A5C] hover:text-white'}`}>
      {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileDown className="w-3 h-3" />} {err ? 'FEHLER' : 'PDF'}
    </button>
  );
};

export const ZahlungenKarte = ({ zahlungen, mnr, caseId }) => {
  const { summe = {}, items = [] } = zahlungen || {};
  return (
    <section className="bg-white border border-gray-200 rounded-2xl overflow-hidden" data-testid="m360-zahlungen">
      <header className="flex items-center gap-2 px-4 py-2.5 border-b border-gray-100">
        <Wallet className="w-4 h-4 text-[#b8893d]" />
        <h3 className="text-[11px] tracking-[0.2em] text-[#1B3A5C] font-semibold uppercase">Zahlungen</h3>
        <span className="text-[11px] text-[#7a8a99]">{items.length}</span>
        <PdfBtn mnr={mnr} caseId={caseId} />
      </header>
      <div className="p-3 space-y-2">
        <div className="flex flex-wrap gap-2">
          <Summe k="bezahlt" label="Bezahlt" wert={summe.bezahlt} />
          <Summe k="offen" label="Offen" wert={summe.offen} />
          <Summe k="ueberfaellig" label="Überfällig" wert={summe.ueberfaellig} />
        </div>
        {items.length === 0 ? <p className="text-xs text-[#7a8a99]" data-testid="m360-zahlungen-leer">Noch keine Zahlungen.</p>
          : <ul className="divide-y divide-gray-50">{items.map((z) => <Zeile key={`${z.art}-${z.id}`} z={z} mnr={mnr} />)}</ul>}
      </div>
    </section>
  );
};
